import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Zap, Target, ChevronRight, Star, Shield, Sword, Trophy, Award, ArrowRight } from "lucide-react";

const MiniLogo = ({ name, size = "md" }) => {
  const colors = ["bg-violet-600", "bg-purple-600", "bg-fuchsia-600", "bg-indigo-600", "bg-violet-500", "bg-purple-500", "bg-pink-600", "bg-indigo-500"];
  const i = name ? name.charCodeAt(0) % colors.length : 0;
  const s = size === "sm" ? "w-9 h-9 text-xs" : "w-11 h-11 text-sm";
  return <div className={`${s} rounded-xl ${colors[i]} flex items-center justify-center flex-shrink-0 border border-white/10 shadow-lg shadow-purple-500/20`}><span className="text-white font-bold">{name?.charAt(0)?.toUpperCase()}</span></div>;
};

const XPPill = ({ value }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 text-[10px] font-bold border border-violet-500/30">
    <Zap className="w-2.5 h-2.5" />+{value} XP
  </span>
);

export const QuestView = ({ data, user, onOfferClick }) => {
  const { getStatus } = useUnifiedStatus();
  const { offers, expiringSoon, bestValue, stats, favoriteIds, toggleFavorite } = data;
  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);

  // XP calculation — value * ease
  const getXP = (offer) => Math.round((offer.offer_value_est || 0) * (offer.ease_stars || 1));
  const totalXP = offers.reduce((s, o) => s + getXP(o), 0);

  // Progress ring calc
  const daysProgress = Math.max(0, Math.min(100, 100 - (stats.days_until_birthday / 365 * 100)));
  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (daysProgress / 100) * circumference;

  // Quests: daily challenges
  const dailyQuests = [
    ...(expiringSoon.length > 0 ? [{ type: "urgent", label: "Rescue Expiring Loot", offers: expiringSoon.slice(0, 3), xpBonus: 50 }] : []),
    { type: "easy", label: "Quick Wins", offers: offers.filter(o => o.ease_stars >= 4).slice(0, 4), xpBonus: 25 },
    { type: "value", label: "High-Value Raids", offers: bestValue.slice(0, 4), xpBonus: 100 },
    { type: "all", label: "Full Inventory", offers: offers.slice(0, 8), xpBonus: 0 },
  ];

  return (
    <div className="min-h-screen bg-slate-950 pb-28" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {/* XP Ring hero */}
      <div className="flex flex-col items-center pt-8 pb-4 relative">
        {/* Glow effect */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-violet-600/20 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative">
          <svg width="140" height="140" className="transform -rotate-90">
            <circle cx="70" cy="70" r="54" fill="none" stroke="rgba(139,92,246,0.15)" strokeWidth="8" />
            <motion.circle
              cx="70" cy="70" r="54" fill="none" stroke="url(#xpGradient)" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={circumference}
              initial={{ strokeDashoffset: circumference }}
              animate={{ strokeDashoffset }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
            <defs>
              <linearGradient id="xpGradient" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#d946ef" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-3xl font-bold text-white">{stats.days_until_birthday}</p>
            <p className="text-[10px] text-violet-400 uppercase tracking-widest">Days Left</p>
          </div>
        </div>

        <div className="mt-3 text-center">
          <p className="text-white font-bold text-lg">{user?.first_name}</p>
          <div className="flex items-center gap-2 justify-center mt-1">
            <span className="text-xs text-violet-400">Level {Math.floor(totalXP / 500) + 1}</span>
            <span className="text-slate-600">|</span>
            <span className="text-xs text-fuchsia-400">{totalXP.toLocaleString()} XP</span>
          </div>
        </div>

        {/* Stats bar */}
        <div className="flex items-center gap-4 mt-4 px-5 py-3 bg-slate-900/80 rounded-2xl border border-slate-800">
          <div className="text-center">
            <p className="text-white font-bold">{offers.length}</p>
            <p className="text-[10px] text-slate-500">Quests</p>
          </div>
          <div className="w-px h-8 bg-slate-800" />
          <div className="text-center">
            <p className="text-emerald-400 font-bold">${totalValue.toFixed(0)}</p>
            <p className="text-[10px] text-slate-500">Loot</p>
          </div>
          <div className="w-px h-8 bg-slate-800" />
          <div className="text-center">
            <p className="text-red-400 font-bold">{expiringSoon.length}</p>
            <p className="text-[10px] text-slate-500">Urgent</p>
          </div>
          <div className="w-px h-8 bg-slate-800" />
          <div className="text-center">
            <p className="text-pink-400 font-bold">{favoriteIds.size}</p>
            <p className="text-[10px] text-slate-500">Saved</p>
          </div>
        </div>
      </div>

      {/* Quest sections */}
      <div className="px-5 mt-4 space-y-5">
        {dailyQuests.map((quest) => (
          <div key={quest.type}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Target className={`w-4 h-4 ${quest.type === "urgent" ? "text-red-400" : quest.type === "easy" ? "text-amber-400" : quest.type === "value" ? "text-emerald-400" : "text-slate-400"}`} />
                <h2 className="text-sm font-bold text-white">{quest.label}</h2>
              </div>
              {quest.xpBonus > 0 && <XPPill value={quest.xpBonus} />}
            </div>
            <div className="space-y-2">
              {quest.offers.map((offer, i) => (
                <motion.div
                  key={offer.business_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => onOfferClick(offer)}
                  className="flex items-center gap-3 p-3 bg-slate-900/60 rounded-xl border border-slate-800/50 cursor-pointer hover:border-violet-500/30 hover:bg-slate-900/80 transition-all group"
                  data-testid={`quest-offer-${offer.business_id}`}
                >
                  <MiniLogo name={offer.name} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{offer.name}</p>
                    {getStatus(offer.business_id) && <UnifiedStatusBadge status={getStatus(offer.business_id)} />}
                    <p className="text-xs text-slate-500 truncate">{offer.offer_title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <XPPill value={getXP(offer)} />
                    <button
                      onClick={(e) => { e.stopPropagation(); onOfferClick(offer); }}
                      className="px-3 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-bold rounded-lg transition-colors shadow-lg shadow-violet-600/20"
                      data-testid={`quest-claim-${offer.business_id}`}
                    >
                      Claim
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
